import { Client } from '@twilio/conversations'
import {
  logError,
  logMessage,
  getChatLogLevel,
  updateDocumentTitle,
  getChatIdentity,
} from './helpers'
import store from 'src/app/store'
import { updateUnreadCount } from 'src/modules/Chat/actions'
import PushService from 'src/utils/push-service'
import { getRequest, postRequest } from './request-client'
import QueueService from './queue-service'
import intlMessages from 'src/modules/Chat/translations'
import idbKeyval from 'idb-keyval'

class ChatService {
  _sendingMessage = false
  _channel = undefined
  _client = null
  numRetries = 10
  chatLogLevel = getChatLogLevel()
  initResponseChatData = undefined
  channelName = ''
  identity = ''
  user = undefined
  instance = undefined
  intl = undefined
  history = undefined
  messageAddedListener = null
  typingStartedListener = null
  typingStoppedListener = null
  _onMessageAddedCb = () => {}
  _onChannelCreatedCb = () => {}

  /**
   * Configure and init the chat.
   *
   * @param {object} intl- internationalization object
   * @param {object} history - browser history
   * @param {string} channelName
   * @param {string} identity
   * @return {Promise<object>}
   */

  async initialize(intl, history, channelName, identity) {
    return new Promise(async (resolve, reject) => {
      try {
        if (intl) {
          this.intl = intl
        }
        if (history) {
          this.history = history
        }
        this.identity = identity
        this.channelName = channelName
        const chatData = await this.getChatData()
        if (!channelName) {
          this.channelName = chatData.channelName
        }
        this.initResponseChatData = chatData
        // Exit and return response containing error
        if (!chatData || !chatData.token || chatData.success === false) {
          reject(this.chatData)
        }

        // Set Twilio event listeners
        this.onMessageAdded(this._onMessageAddedCb)

        const clientResponse = await this._getOrCreateClient(chatData.token).catch(e => {
          logError('Unable to create Twilio client', e)
          return {
            success: false,
            error: e,
          }
        })

        // Exit and return response containing error - if chat service not configured properly
        if (
          (clientResponse.hasOwnProperty('error') && clientResponse.error) ||
          !clientResponse.client
        ) {
          reject(clientResponse)
        }

        // Update client
        this._client = clientResponse.client
        this._channel = await this._client.getConversationByUniqueName(this.channelName)
        this._client.on('tokenExpired', async () => {
          // Sentry.captureMessage('Twilio token expired')
          // const chatData = await _getChatData(_identity)
          // if (chatData && chatData.success !== false) {
          //   store.dispatch(setChatData(chatData))
          //   _updateToken(chatData.token).catch((e) => {
          //     logError('Error updating Twilio token', e)
          //   })
          // }

          logMessage(
            'Twilio token expired, redirecting to home page.',
            {
              identity: this.identity,
              client: this._client,
              history: this.history,
            },
            2,
          )

          // The above didn't work, so we'll just redirect to hope page which will require chat to be re-initialized
          if (this.history) {
            this.history.push('/')
          } else {
            window.location.reload()
          }
        })

        // Success
        resolve({
          success: true,
          error: null,
          chatData: chatData,
          client: this._client,
        })
      } catch (e) {
        logError('Configure twilio service and set event listeners', e)
        reject({
          success: false,
          error: e,
        })
      }
    })
  }

  /**
   * Checks if channel exists and returns it if it does
   *
   * @returns {Promise<object>} Either gets chat details or error
   */
  async getChatData() {
    return getRequest({
      base: '/chat/data',
      mock: false,
      query: [
        {
          name: 'identity',
          value: encodeURIComponent(this.identity),
        },
        {
          name: 'channelName',
          value:
            this.channelName && this.channelName.length
              ? encodeURIComponent(this.channelName)
              : null,
        },
      ],
    })
      .then(data => {
        try {
          return {
            channelName: data.channelName,
            staffMembers: data.chat.hasOwnProperty('staff') ? data.chat.staff : data.chat.avatars,
            headerMessage: data.chat.hasOwnProperty('message') ? data.chat.message : null,
            token: data.token,
          }
        } catch (e) {
          return {
            success: false,
            error: e,
          }
        }
      })
      .catch(e => {
        logError('Get chat data request', { error: e, errorResponse: e.response })
        return {
          success: false,
          error: e,
        }
      })
  }
  onMessageAdded(cb) {
    this._onMessageAddedCb = cb
  }
  onChannelCreated(cb) {
    this._onChannelCreatedCb = cb
  }

  /**
   * Returns object containing client or caught error
   *
   * @param {string} token
   * @returns {Promise<object>}
   */
  async _getOrCreateClient(token) {
    return new Promise(async (resolve, reject) => {
      // Continue getting Twilio client if not already defined
      if (!this._client) {
        // Options for Twilio Client
        let clientOptions = {}

        // Allow setting 'chatLogLevel' from local storage (to enable Twilio debug just while testing), e.g. 'debug'
        const chatLogLevel = getChatLogLevel()
        if (chatLogLevel) {
          clientOptions.logLevel = chatLogLevel
        }

        const createClient = new Client(token, clientOptions)
        createClient.on('stateChanged', state => {
          if (state === 'failed') {
            logError('Create twilio client', state)
            return {
              clientCreateError: state,
            }
          }
        })

        // Return error if Twilio client couldn't be created
        if (createClient && createClient.hasOwnProperty('clientCreateError')) {
          reject({
            success: false,
            client: null,
            error: createClient.clientCreateError,
          })
        }

        // Update current client
        this._client = createClient
      }

      resolve({
        success: true,
        client: this._client,
      })
    })
  }

  async getMessages() {
    try {
      const channel = await this._getChannelByUniqueName(this.channelName)
      if (!channel) {
        return []
      }

      return channel.getMessages(50)
    } catch (e) {
      logError('Get messages in chat service', e)
      return []
    }
  }
  async _getChannelByUniqueName(uniqueName, isLastSendingAttempt = false) {
    try {
      if (!this._channel && this._client) {
        this._channel = await this._client.getConversationByUniqueName(uniqueName).catch(e => {
          const errorMessage =
            '[Getting or creating channel] [Note: Fine for empty chat and while sending the first message]'

          if (isLastSendingAttempt === true || e.toString() !== 'Error: Not Found') {
            logError(errorMessage, e)
          } else {
            logMessage(errorMessage, e, 0)
          }

          return null
        })
      }

      this.addChannelEventListeners()

      return this._channel
    } catch (e) {
      logError('Unable to get channel', e)
      return null
    }
  }
  addChannelEventListeners = isNewListener => {
    try {
      if (!this._channel) {
        return false
      }
      // Set "message added" listener only if not already set and if channel exists (if new conversation has been initialized)
      if (!this.messageAddedListener || isNewListener) {
        this.messageAddedListener = this._channel.on('messageAdded', this._onMessageAddedCb)
      }

      // Bad way to add listeners only once, but it's consistent with "messageAdded" listener
      if (!this.typingStartedListener) {
        // When chat member on the other side starts typing
        // eslint-disable-next-line no-unused-vars
        this.typingStartedListener = this._channel.on('typingStarted', member => {
          // Get parentId of chat member which is typing
          // const parentId = member.state && member.state.attributes && member.state.attributes.parentId

          // Extract CSR/Staff Member name
          // const memberName = getStaffMemberAttribute('name', 'csrDataId', parentId, staffMembers)

          // New title containing member name or just generic message if member not found
          // const typingTitle = memberName ? memberName + ' is typing...' : 'Typing...'

          const typingTitle = this.intl.formatMessage(intlMessages.chatStatusTyping)

          updateDocumentTitle(null, typingTitle, 0)
        })
      }

      if (!this.typingStoppedListener) {
        // When chat member on the other side stops typing
        this.typingStoppedListener = this._channel.on('typingEnded', () => {
          // Either reset title to default or set "unread" messages title
          this.updateUnreadStates(this._channel)
        })
      }
    } catch (e) {
      logError('Typing started or ended', e)
    }
  }

  /**
   * Update "unread" counter on all the places:
   * * Document / Browser Tab Title (document.title)
   * * IndexedDB
   * * App Badge
   *
   * @param channel
   * @return {void}
   */
  async updateUnreadStates() {
    try {
      const total = this._channel ? await this._channel.getMessagesCount() : 0
      const lastMessageIndex = this._channel && this._channel.lastReadMessageIndex
      const unreadCount = lastMessageIndex === null ? total : total - lastMessageIndex - 1
      const safeUnreadCount = unreadCount && unreadCount >= 0 ? unreadCount : 0

      const messageCountTitle =
        safeUnreadCount > 0
          ? this.intl.formatMessage(intlMessages.chatNewMessages, {
              unreadMessages: safeUnreadCount,
            })
          : null

      // Update document title
      updateDocumentTitle(null, messageCountTitle)

      // Update global redux state
      store.dispatch(updateUnreadCount(safeUnreadCount))

      // Update App Badge (when added as standalone app)
      if (safeUnreadCount > 0) {
        PushService.setAppBadge(safeUnreadCount)
      } else {
        PushService.clearAppBadge()
      }

      // Update IndexedDB, to be readable from everywhere (including Service Worker)
      this._storeUnreadChatMessages(safeUnreadCount)
    } catch (e) {
      logError('Update unread states', e)
    }
  }

  /**
   * Update unread message count to IndexedDB which is readable from ServiceWorker.
   *
   * Saving for each chat identity, to get correct count when chat resets (on logout etc.)
   *
   * @param {number} unreadCount
   * @return {boolean|void}
   */
  _storeUnreadChatMessages(unreadCount) {
    try {
      if (!this.identity) {
        return false
      }

      // Get existing values
      idbKeyval.get('unread-chat-messages').then(existingState => {
        const parsedExistingState = existingState ? JSON.parse(existingState) : {}

        const unreadChatMessages = JSON.stringify({
          ...parsedExistingState,
          [this.identity]: unreadCount,
        })

        // Save new values
        idbKeyval.set('unread-chat-messages', unreadChatMessages)
      })
    } catch (e) {
      logError('Store unread chat messages', e)
    }
  }

  /**
   * Checks if channel exists and returns it if it does
   *
   * @param {string} channelName
   * @returns {Promise<mixed>}
   */
  async channelExists(channelName) {
    const channel = await this._getChannelByUniqueName(channelName)
    if (!channel) {
      return Promise.resolve()
    }
    return Promise.resolve(channel)
  }

  sendBotMessage(contentType, body, retry = 0) {
    return new Promise(async (resolve, reject) => {
      if (retry >= this.numRetries) {
        return reject({
          message: 'Reached ' + this.numRetries + ' send attempts',
          shouldOfferRetry: true,
        })
      }

      logMessage('Sending bot message', { _sendingMessage: this._sendingMessage, retry })

      // Since sending bot message is trigger on channel creation, we want to wait for other messages to be sent before sending our bot message
      if (this._sendingMessage) {
        setTimeout(() => {
          this.sendBotMessage(contentType, body, retry + 1)
            .then(() => {
              // Return true because this is just retrying
              resolve(true)
            })
            .catch(e => {
              reject({
                error: e,
                message: 'Catch sendBotMessage.apply [retry: ' + retry + ']',
                shouldOfferRetry: false,
              })
            })
        }, 1500)
        return
      }
      this._sendingMessage = true
      retry = 0
      this._sendMessageToServer
        .apply({ channelName: this.channelName, body, from: 'bot', contentType })
        .then(response => {
          this._sendingMessage = false
          resolve(response)
        })
        .catch(e => {
          this._sendingMessage = false
          reject({
            error: e,
            message: 'Catch _sendMessageToServer.apply',
            shouldOfferRetry: true,
          })
        })
    })
  }

  /**
   * Special function used by queue, so we don't want to use fat arrow here
   */
  _sendMessageToServer() {
    const params = {
      channelName: this.channelName,
      identity: this.from,
      from: this.from,
      fromMemberType: 'visitor',
      body: this.body,
      attributes: {
        contentType: this.contentType,
      },
    }

    logMessage('Send message to server')

    return postRequest(
      {
        base: '/chat/message',
        mock: false,
      },
      params,
    ).catch(function(e) {
      logError('Send message to server request', { error: e, errorResponse: e.response })
      return false
    })
  }
  async sendMessage(body, contentType, userEmail = null) {
    if (this.chatLogLevel === 'debug') {
      logMessage('[Chat] Send message start')
    }

    // add to queue
    QueueService.enqueue({ body, contentType })
    const executeMessage = (body, contentType, retry = 0) => {
      if (this.chatLogLevel === 'debug') {
        logMessage('[Chat] Execute message [retry: ' + retry + ']')
      }

      return new Promise(async (resolve, reject) => {
        if (retry >= this.numRetries) {
          return reject({
            message: 'Reached ' + this.numRetries + ' send attempts',
            shouldOfferRetry: true,
          })
        }
        if (!this.channelName) {
          logMessage('Channel not found when trying to send a message', null, 2)
        }

        const isLastSendingAttempt = retry >= this.numRetries - 1
        let channel = await this._getChannelByUniqueName(this.channelName, isLastSendingAttempt)
        if (!channel) {
          const channelCreated = await this._createNewChannel(userEmail)
          if (!channelCreated) {
            reject({
              message: 'Create new channel failed [retry: ' + retry + ']',
              shouldOfferRetry: true,
            })
          }
        }

        if (!channel && !this._sendingMessage) {
          setTimeout(() => {
            executeMessage
              .apply(null, [body, contentType, retry + 1])
              .then(() => {
                // Return true because this is just retrying
                resolve(true)
              })
              .catch(e => {
                logMessage('Execute message failed [retry: ' + retry + ']', e)
                reject({
                  error: e,
                  message: 'Catch executeMessage.apply [retry: ' + retry + ']',
                  shouldOfferRetry: false,
                })
              })
          }, 1500)
          return
        }

        this._sendingMessage = true
        const message = QueueService.dequeue()
        const identity = getChatIdentity()
        this._sendMessageToServer
          .apply({
            channelName: this.channelName,
            body: message.body,
            from: identity,
            contentType: message.contentType,
          })
          .then(response => {
            this._sendingMessage = false
            resolve(response)
          })
          .catch(e => {
            this._sendingMessage = false
            logMessage('Send message to server failed', e)
            reject({
              error: e,
              message: 'Catch _sendMessageToServer.apply',
              shouldOfferRetry: true,
            })
          })
      })
    }

    return executeMessage(body, contentType).catch(e => {
      logError('Execute send message', e)
      return false
    })
  }

  /**
   * @returns {Promise<boolean>}
   */
  _createNewChannel(userEmail = null) {
    const identity = this.identity || getChatIdentity()
    return postRequest(
      {
        base: '/chat/new-channel',
        mock: false,
      },
      {
        channelName: this.channelName,
        userEmail,
        identity,
      },
    )
      .then(() => {
        logMessage('Chat channel created')
        this._onChannelCreatedCb(this.channelName)
        return true
      })
      .catch(e => {
        logError('Create new channel request', e)
        return false
      })
  }

  /**
   * Update Twilio's "last read message" and all of our states (storage, UI, etc.).
   *
   * @param message
   * @return {Promise<boolean>}
   */
  async updateLastConsumedAndUnreadStates(message = null) {
    try {
      // If there is no channel, but we have it in new message
      if (!this._channel && message && message.channel) {
        // Update chanel
        this._channel = message.channel
      }

      if (!this._channel) {
        return false
      }

      const messageState = message && message.state

      // Currently will just mark all messages as read
      await this._updateLastReadMessageIndex(messageState)

      // Update counter globally (title, storage, etc.)
      this.updateUnreadStates()
    } catch (e) {
      logError('Update read messages status', e)
    }
  }

  /**
   * Update Twilio's "last read message", so we can later check number of unread messages.
   *
   * This index is just a message "position" in the list of received messages, in relation to all existing messages.
   *
   * Unread messages is a number of new messages since this index.
   * It will include sent messages, so we need to call this function when sending a message.
   *
   * Example for setting first three fetched messages (getMessages()) as read, would be: messages.items[2].index
   *
   * @param {object} messageState Last read message state (needs to contain "index" property)
   * @return {Promise<boolean|*>}
   */
  async _updateLastReadMessageIndex(messageState = null) {
    try {
      if (!this._channel) {
        return false
      }

      let lastReadMessageIndex = 0

      // If message is specified, take that as last read message
      if (messageState && messageState.index) {
        lastReadMessageIndex = messageState.index
      } else {
        // Try to find last message in this chat
        if (this._channel && this._channel.lastMessage && this._channel.lastMessage.index) {
          lastReadMessageIndex = this._channel.lastMessage.index
        }
      }

      return await this._channel.updateLastReadMessageIndex(lastReadMessageIndex)
    } catch (e) {
      logError('Update last read message index', e)
    }
  }

  /**
   * Resets "unread" counter on all the places:
   * * Twilio channel state
   * * Document / Browser Tab Title (document.title)
   * * IndexedDB
   * * App Badge
   *
   * @return {void}
   */
  async resetUnreadStates() {
    try {
      // Currently will just mark all messages as read
      await this._updateLastReadMessageIndex()

      // Update document title
      updateDocumentTitle()

      // Update global redux state
      store.dispatch(updateUnreadCount(0))

      PushService.clearAppBadge()

      // Update IndexedDB, to be readable from everywhere (including Service Worker)
      this._storeUnreadChatMessages(0)
    } catch (e) {
      logError('Reset unread counter', e)
    }
  }

  /**
   * Based on twilio docs: https://www.twilio.com/docs/chat/initializing-sdk-clients
   * Client.shutdown() is required when it's no longer needed
   *
   * Currently not used, because we wan't to keep it active to receive various events.
   *
   * Can be used in componentWillUnmount()
   *
   * @returns void
   *
   */
  shutdown = () => {
    try {
      if (this._client) {
        // Shut down Twilio Client
        this._client.shutdown()
        // Reset Twilio Client so it can be re-initialized properly
        this._client = null
      }
      // Reset chanel so it can be re-initialized properly
      this._channel = null
      // Remove this if messages for some reason start showing up 2 times
      this.messageAddedListener = null
      this.typingStartedListener = null
      this.typingStoppedListener = null
    } catch (e) {
      logError('Chat client shutdown', e)
    }
  }

  /**
   * Used only when anonymous visitor leaves an email (which becomes friendly name).
   *
   * @param {string} friendlyName Email from "Get notified by email" form
   * @param {string} channelName Current chat channel
   * @returns {Promise<void>}
   */
  updateChannel(friendlyName, channelName) {
    return postRequest(
      { base: '/chat/update-channel', mock: false },
      {
        friendlyName,
        channelName,
      },
    )
  }

  /**
   * Sets everything to initial data.
   * **/
  reset() {
    this._sendingMessage = false
    this._channel = undefined
    this._client = null
    this.numRetries = 10
    this.chatLogLevel = getChatLogLevel()
    this.initResponseChatData = undefined
    this.channelName = ''
    this.identity = ''
    this.user = undefined
    this.instance = undefined
    this.intl = undefined
    this.history = undefined
    this.messageAddedListener = null
    this.typingStartedListener = null
    this.typingStoppedListener = null
    this._onMessageAddedCb = () => {}
    this._onChannelCreatedCb = () => {}
  }
}

export default new ChatService()
